import { ArrowRight, CircleDot } from "lucide-react";
import Reveal from "./Reveal";
import { useWaitlist } from "../context/WaitlistContext";

const TONES = {
  live: "border-ember/40 bg-ember/15 text-ember",
  beta: "border-ember/30 bg-ember/10 text-ember/90",
  soon: "border-white/20 bg-white/5 text-paper",
  plan: "border-white/10 bg-transparent text-mute",
};

const items = [
  {
    status: "Live",
    tone: "live",
    title: "Local memory engine — on PyPI",
    desc: "Content-addressed ingestion, the Context Rehydrator (semantic + temporal), 12 CLI commands and a local knowledge graph. Install it today: pip install upii (v0.7.0).",
  },
  {
    status: "In beta",
    tone: "beta",
    title: "Relational retrieval + ambient inbox",
    desc: "Knowledge-graph re-ranking across people, projects and organisations, plus consent-gated ambient capture that flows through an approval inbox before entering durable memory.",
  },
  {
    status: "Coming soon",
    tone: "soon",
    title: "MCP bridge for agents",
    desc: "Give Claude, Cursor and other agents smritium_search / smritium_ask / smritium_list_sources — returning only user-approved, cited chunks. The corpus never leaves the device.",
  },
  {
    status: "Coming soon",
    tone: "soon",
    title: "Cmd+K everywhere + encrypted sync",
    desc: "A lightweight overlay puts your memory one keystroke away in any app, with multi-device sync over LAN / encrypted P2P — no cloud in the loop.",
  },
  {
    status: "Coming soon",
    tone: "soon",
    title: "Mail & calendar connectors",
    desc: "Consent-gated capture from mail (.mbox / .eml) and calendar (.ics), with debouncing, dedup and delete-handling.",
  },
  {
    status: "Planned",
    tone: "plan",
    title: "Smritium Membrane — Enterprise",
    desc: "Org-wide egress audit + PII redaction, on-prem / air-gapped deployment, and DPDP / GDPR compliance reporting your security team can actually read.",
  },
  {
    status: "Planned",
    tone: "plan",
    title: "Answer verification & smritium forget",
    desc: "Abstention when confidence is low, and user-facing verified deletion — remove a memory and prove it's gone, end to end.",
  },
];

export default function Roadmap() {
  const { openWaitlist } = useWaitlist();
  return (
    <section
      id="roadmap"
      className="scroll-mt-32 border-t border-white/5 py-24 md:py-28"
      data-testid="roadmap"
    >
      <div className="container-x">
        <Reveal>
          <p className="overline">The road ahead</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-light leading-tight tracking-tight text-paper md:text-[2.6rem]">
            What's upcoming on smritium.com
          </h2>
          <p className="mt-5 max-w-2xl text-lg font-light leading-relaxed text-mute">
            The open-source core is live today. Here's what's shipping next as
            Smritium grows from a local memory engine into a full sovereign
            substrate for people and enterprises.
          </p>
        </Reveal>

        <div className="mt-14 space-y-4">
          {items.map((it, i) => (
            <Reveal key={it.title} delay={i * 0.04}>
              <div className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-void/60 p-6 transition-[border-color] duration-300 hover:border-ember/30 md:flex-row md:items-center md:gap-6">
                <div className="shrink-0 md:w-40">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-wide ${TONES[it.tone]}`}
                  >
                    <CircleDot size={11} /> {it.status}
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-xl text-paper">{it.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-mute">{it.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 flex flex-col items-center gap-4 text-center">
            <p className="text-mute">Want early access to each drop as it lands?</p>
            <button
              onClick={() => openWaitlist()}
              className="btn-primary"
              data-testid="roadmap-cta"
            >
              Get beta invites <ArrowRight size={16} />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
